/** Mobile variant navigation and error presentation; native forms own purchases. */
class MidecMobileProductLayout extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('[data-midec-mobile-product]');
    if (!this.root) return;
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.clearPending = () => {
      this.pending = null;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
    };

    this.optionChanged = (event) => {
      if (!event.isTrusted || !this.mobile.matches || !(event.target instanceof Element)) return;
      const picker = event.target.closest('.product-details variant-picker');
      if (!picker || !this.root.contains(picker) || !event.target.matches('input[type="radio"]:checked, select')) return;
      this.clearPending();
      this.pending = { picker, key: this.selectionKey(picker), expires: performance.now() + 30000 };
    };

    this.variantUpdated = (event) => {
      const pending = this.pending;
      if (!pending || event.target !== pending.picker) return;
      if (!this.mobile.matches || performance.now() > pending.expires) {
        this.clearPending();
        return;
      }
      const updatedPicker = event.detail?.data?.html?.querySelector('variant-picker');
      if (!updatedPicker || this.selectionKey(updatedPicker) !== pending.key) return;

      // Consume once. Duplicate update events cannot schedule another scroll.
      this.pending = null;
      // Native price, gallery and availability listeners finish this event first.
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (!this.isConnected || !this.mobile.matches || !pending.picker.isConnected ||
            this.selectionKey(pending.picker) !== pending.key) return;
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
      });
    };

    this.presentError = (event) => {
      const description = event.detail?.data?.description;
      const form = this.root.querySelector('.product-details .buy-buttons-block');
      if (!this.mobile.matches || !form?.contains(event.target) || typeof description !== 'string' || !description.trim()) return;
      queueMicrotask(() => {
        if (!this.isConnected || !this.mobile.matches) return;
        const error = form.querySelector('[ref="addToCartTextError"]');
        const liveRegion = form.querySelector('[ref="liveRegion"]');
        if (error) {
          const text = Array.from(error.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
          if (text) text.textContent = description;
          else error.appendChild(document.createTextNode(description));
        }
        if (liveRegion) liveRegion.textContent = description;
      });
    };

    // Capture the real customer change before the picker starts its native request.
    this.root.addEventListener('change', this.optionChanged, true);
    this.root.addEventListener('variant:update', this.variantUpdated);
    this.root.addEventListener('cart:error', this.presentError);
    this.mobile.addEventListener('change', this.clearPending);
  }

  selectionKey(picker) {
    return JSON.stringify(Array.from(picker.querySelectorAll('input[type="radio"]:checked, select'), control => {
      const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
      return option?.dataset.optionValueId || control.value;
    }));
  }

  disconnectedCallback() {
    this.clearPending?.();
    this.root?.removeEventListener('change', this.optionChanged, true);
    this.root?.removeEventListener('variant:update', this.variantUpdated);
    this.root?.removeEventListener('cart:error', this.presentError);
    this.mobile?.removeEventListener('change', this.clearPending);
  }
}

if (!customElements.get('midec-mobile-product-layout')) {
  customElements.define('midec-mobile-product-layout', MidecMobileProductLayout);
}
